import React from "react";

import { keyframes } from "@emotion/react";

import {
  Box,
  type ButtonProps as ChakraButtonProps,
  Button as ChakraButton,
  Flex,
  Text,
  Tooltip
} from "@chakra-ui/react";

import { dotLoaderJson, DotLoader, useDotLoadingState } from "@euka-mfes/bark";

export interface ButtonProps extends ChakraButtonProps {
  children: React.ReactNode;
  disableReason?: string;
  loadingText?: string;
  animation?: "pulse" | "spin" | "pulseWithDots";
  src?: string;
}

// Define the pulse animation keyframes
const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const Button = ({
  children,
  loading,
  loadingText,
  disabled,
  size,
  variant,
  disableReason,
  animation = "spin",
  src = dotLoaderJson,
  ...buttonProps
}: ButtonProps) => {
  const showLoading = useDotLoadingState({ loading: loading || false });

  const chakraButtonProps = {
    ...buttonProps,
    size,
    variant,
    disabled,
    ...(showLoading ? { "data-loading": true } : {})
  };

  // Determine text color based on variant
  const getLoadingTextColor = () => {
    const hollowVariants = [
      "primaryHollow",
      "secondaryHollow",
      "tertiaryHollow"
    ];

    if (hollowVariants.includes(variant as string)) {
      return "button.label.hollow";
    }

    return "base.300";
  };

  const loadingTextColor = getLoadingTextColor();

  const getSpinner = () => {
    if (animation === "pulse" || animation === "pulseWithDots") {
      loadingText = loadingText || "Loading...";
      return (
        <Flex gap={4} alignItems="center">
          {animation === "pulseWithDots" && (
            <DotLoader
              size="lg"
              direction="row"
              src={src}
              overlayStyle={{ marginTop: 8 }}
            />
          )}
          <Box
            animation={`${pulseAnimation} 2s ease-in-out infinite`}
            data-testid="animation"
          >
            <Text
              textStyle="heading.standard"
              fontSize="md"
              color={loadingTextColor}
            >
              {loadingText}
            </Text>
          </Box>
        </Flex>
      );
    }

    return (
      <DotLoader
        size="md"
        textGap={loadingText ? 6 : 8}
        loadingText={loadingText}
        textColor={loadingTextColor}
        src={src}
      />
    );
  };

  const getLoadingText = () => {
    if (!loadingText) {
      return "";
    }
    switch (animation) {
      case "spin":
        return <Text fontSize="xs">{loadingText}</Text>;
      case "pulse":
        return (
          <Text
            textStyle="heading.standard"
            fontSize="md"
            color={loadingTextColor}
          >
            {loadingText}
          </Text>
        );
      case "pulseWithDots":
        return (
          <Text
            px={10}
            textStyle="heading.standard"
            fontSize="md"
            color={loadingTextColor}
          >
            {loadingText}
          </Text>
        );
    }
  };

  const buttonContent = (
    <ChakraButton
      {...chakraButtonProps}
      loading={showLoading}
      spinner={getSpinner()}
    >
      {showLoading ? getLoadingText() : children}
    </ChakraButton>
  );

  return disabled && disableReason ? (
    <Tooltip.Root positioning={{ placement: "top" }}>
      <Tooltip.Trigger asChild>{buttonContent}</Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>
          <Tooltip.Arrow>
            <Tooltip.ArrowTip />
          </Tooltip.Arrow>
          {disableReason}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  ) : (
    buttonContent
  );
};

export default Button;
